import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { usePageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { PeriodCloseCard } from '@/components/finance/PeriodCloseCard';
import { errorMessage } from '@/lib/errors';
import { financePeriodsQuery } from '@/lib/finance';
import { meQuery, usePermissions } from '@/lib/me';
import { FINANCE_PERIOD_STATUS_TONE } from '@/lib/status-tone';

export const Route = createFileRoute('/_app/finance/periods')({ component: PeriodsPage });

function PeriodsPage() {
  return <PeriodsScreen />;
}

// Route/screen split, same as finance.queue.tsx — a pure-props screen is
// testable without a mounted router.
export function PeriodsScreen() {
  const { can } = usePermissions();
  const me = useQuery(meQuery);
  const periods = useQuery(financePeriodsQuery);
  const [selected, setSelected] = useState<string | undefined>(undefined);

  usePageHeader({ subtitle: periods.data ? `${periods.data.length} months` : undefined });

  if (!can('finance.read') || errorStatus(periods.error) === 403) return <AccessDenied />;

  if (periods.isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (periods.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {errorMessage(periods.error)}
      </p>
    );
  }

  // D-C1 (final review): the wire value is `YYYY-MM-01` (ogen's date
  // encoder, api/openapi.yaml `format: date`) while every path/query use
  // (GET/close/reopen a period, the queue's `month` search param) wants
  // `YYYY-MM` (`pattern: '^\d{4}-\d{2}$'`). Normalise once here so every
  // downstream read of `list[].month` already has the path shape.
  const list = [...(periods.data ?? [])]
    .map((p) => ({ ...p, month: p.month.slice(0, 7) }))
    .sort((a, b) => a.month.localeCompare(b.month));
  const closedMonths = list.filter((p) => p.status === 'closed').map((p) => p.month);
  // The reopen door only stays open on the SINGLE most recently closed month
  // (server rule, ReopenFinancePeriodErrors' 409 "not the latest closed
  // month") — reopening an older one would leave every closed month after
  // it resting on a snapshot whose own inputs just moved under it.
  const latestClosed = closedMonths.at(-1);
  const isSuperAdmin = (me.data?.roles ?? []).includes('super_admin');

  const month = selected ?? list.find((p) => p.status === 'open')?.month ?? list.at(-1)?.month;

  return (
    <div className="flex flex-col gap-4 sm:flex-row">
      <ul className="flex flex-col gap-1 sm:w-56">
        {list.map((p) => {
          const tone = FINANCE_PERIOD_STATUS_TONE[p.status];
          const active = p.month === month;
          return (
            <li key={p.month}>
              <button
                type="button"
                onClick={() => setSelected(p.month)}
                aria-current={active ? 'true' : undefined}
                className={
                  'flex w-full items-center justify-between gap-2 rounded-[var(--radius-control)] px-3 py-2 text-left text-sm ' +
                  (active ? 'bg-[var(--surface-sunken)] font-semibold' : 'hover:bg-[var(--surface-sunken)]')
                }
              >
                <span>{p.month}</span>
                <Badge variant={tone.variant} size="sm">
                  {tone.label}
                </Badge>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex-1">
        {month && (
          <PeriodCloseCard
            // Fix round 1, item 5 (review Important): key={month} forces a
            // full remount on month switch — without it, PeriodCloseCard's
            // own local blockers/error state (a stale close_blocked list)
            // survives the prop change and shows month A's blockers on
            // month B until another close attempt overwrites them.
            key={month}
            month={month}
            canClose={can('finance.close')}
            canReopen={isSuperAdmin && month === latestClosed}
          />
        )}
      </div>
    </div>
  );
}
